import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminTiles, createTile, updateTile, changePassword } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, LayoutGrid, ArrowUpDown, LogOut, Home, Loader2, Key } from 'lucide-react';
import { toast } from 'sonner';

const TilesAdminPage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTile, setEditingTile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    active: true,
    sortOrder: 1
  });

  const fetchTiles = async () => {
    try {
      const data = await getAdminTiles();
      setTiles(data.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (error) {
      toast.error('Fehler beim Laden der Kacheln');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiles();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwörter stimmen nicht überein');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Neues Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setSaving(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Passwort erfolgreich geändert');
      setIsPasswordDialogOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      url: '',
      active: true,
      sortOrder: tiles.length + 1
    });
    setEditingTile(null);
  };

  const handleOpenDialog = (tile = null) => {
    if (tile) {
      setEditingTile(tile);
      setFormData({
        title: tile.title,
        description: tile.description || '',
        url: tile.url,
        active: tile.active,
        sortOrder: tile.sortOrder
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingTile) {
        await updateTile(editingTile.id, formData);
        toast.success('Kachel erfolgreich aktualisiert');
      } else {
        await createTile(formData);
        toast.success('Kachel erfolgreich erstellt');
      }
      await fetchTiles();
      handleCloseDialog();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-[#0E4C92] text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#0E4C92] font-bold text-lg">GB</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">GB Sylt Portal</h1>
                <p className="text-xs text-white/70">Admin-Bereich</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={() => window.open('/', '_blank')}
              >
                <Home className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Portal ansehen</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={() => setIsPasswordDialogOpen(true)}
              >
                <Key className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Passwort</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E4C92]/10 rounded-lg flex items-center justify-center">
                <LayoutGrid className="w-5 h-5 text-[#0E4C92]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Kacheln verwalten</h2>
                <p className="text-gray-600 text-sm">Erstellen und bearbeiten Sie Portal-Kacheln</p>
              </div>
            </div>
            <Button 
              onClick={() => handleOpenDialog()}
              className="mt-4 sm:mt-0 bg-[#0E4C92] hover:bg-[#0a3a70]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neue Kachel
            </Button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#0E4C92] animate-spin" />
            </div>
          )}

          {/* Tiles Table */}
          {!loading && (
            <Card className="border border-gray-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-16">
                        <ArrowUpDown className="w-4 h-4" />
                      </TableHead>
                      <TableHead>Titel</TableHead>
                      <TableHead className="hidden md:table-cell">Beschreibung</TableHead>
                      <TableHead className="hidden lg:table-cell">URL</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead className="w-24">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tiles.map((tile) => (
                      <TableRow key={tile.id} className={!tile.active ? 'bg-gray-50 opacity-60' : ''}>
                        <TableCell className="font-medium text-gray-500">
                          {tile.sortOrder}
                        </TableCell>
                        <TableCell className="font-medium">{tile.title}</TableCell>
                        <TableCell className="hidden md:table-cell text-gray-600 max-w-xs truncate">
                          {tile.description || '-'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-gray-500 max-w-xs truncate text-sm">
                          {tile.url}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={tile.active ? 'default' : 'secondary'}
                            className={tile.active ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}
                          >
                            {tile.active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(tile)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {tiles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          Keine Kacheln vorhanden. Erstellen Sie Ihre erste Kachel.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            © {new Date().getFullYear()} GB Sylt Portal. Admin-Bereich.
          </p>
        </div>
      </footer>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTile ? 'Kachel bearbeiten' : 'Neue Kachel erstellen'}
            </DialogTitle>
            <DialogDescription>
              {editingTile 
                ? 'Bearbeiten Sie die Eigenschaften der Kachel'
                : 'Füllen Sie die Felder aus, um eine neue Kachel zu erstellen'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Name der Anwendung"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Kurze Beschreibung (optional)"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">URL *</Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sortOrder">Sortierung</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  min="1"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 1 }))}
                />
                <p className="text-xs text-gray-500">Niedrigere Zahlen erscheinen zuerst</p>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="active">Aktiv</Label>
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={saving}>
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#0E4C92] hover:bg-[#0a3a70]" disabled={saving}>
                {saving ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Speichern...
                  </span>
                ) : (
                  editingTile ? 'Speichern' : 'Erstellen'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Passwort ändern</DialogTitle>
            <DialogDescription>
              Geben Sie Ihr aktuelles und neues Passwort ein
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword}>
            <div className="space-y-4 py-4">
              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {passwordError}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Neues Passwort</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-500">Mindestens 6 Zeichen</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setIsPasswordDialogOpen(false);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setPasswordError('');
              }} disabled={saving}>
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#0E4C92] hover:bg-[#0a3a70]" disabled={saving}>
                {saving ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Speichern...
                  </span>
                ) : (
                  'Passwort ändern'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TilesAdminPage;
